import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {brandApi} from '../../../../api/brand/api';
import type {BrandResource, UpdateBrandRequest, CreateBrandRequest, UpdateLogoRequest} from '../../../../api/brand/api.d';
import {getErrorMessage} from '../../../utils/requests/error';

export interface ErrorMessage {
    messageKey: string;
    parameters: Record<string, any>;
}

export interface BrandSettingsState {
    brands: BrandResource[];
    brand: BrandResource | null;
    isBrandsFetching: boolean;
    brandsFetchingError: ErrorMessage | null;
    isBrandFetching: boolean;
    brandFetchingError: ErrorMessage | null;
    isBrandSaving: boolean;
    brandSavingError: ErrorMessage | null;
    isLogoSaving: boolean;
    logoSavingError: ErrorMessage | null;
}

const initialState: BrandSettingsState = {
    brands: [],
    brand: null,
    isBrandsFetching: false,
    brandsFetchingError: null,
    isBrandFetching: false,
    brandFetchingError: null,
    isBrandSaving: false,
    brandSavingError: null,
    isLogoSaving: false,
    logoSavingError: null
};

export const brandSettingsSlice = createSlice({
    name: 'brandSettings',
    initialState,
    reducers: {
        setBrands: (state, action: PayloadAction<BrandResource[]>) => {
            state.brands = action.payload;
        },
        setBrand: (state, action: PayloadAction<BrandResource | null>) => {
            state.brand = action.payload;
        },
        setIsBrandsFetching: (state, action: PayloadAction<boolean>) => {
            state.isBrandsFetching = action.payload;
        },
        setBrandsFetchingError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.brandsFetchingError = action.payload;
        },
        setIsBrandFetching: (state, action: PayloadAction<boolean>) => {
            state.isBrandFetching = action.payload;
        },
        setBrandFetchingError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.brandFetchingError = action.payload;
        },
        setIsBrandSaving: (state, action: PayloadAction<boolean>) => {
            state.isBrandSaving = action.payload;
        },
        setBrandSavingError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.brandSavingError = action.payload;
        },
        setIsLogoSaving: (state, action: PayloadAction<boolean>) => {
            state.isLogoSaving = action.payload;
        },
        setLogoSavingError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.logoSavingError = action.payload;
        }
    }
});

export const {
    setBrands,
    setBrand,
    setIsBrandsFetching,
    setBrandsFetchingError,
    setIsBrandFetching,
    setBrandFetchingError,
    setIsBrandSaving,
    setBrandSavingError,
    setIsLogoSaving,
    setLogoSavingError
} = brandSettingsSlice.actions;

export const fetchBrands = (accountId: string) => async (dispatch: any) => {
    dispatch(setIsBrandsFetching(true));
    dispatch(setBrandsFetchingError(null));

    try {
        const result = await brandApi.getBrands(accountId) as any;
        const brandsList = result.data || [];
        dispatch(setBrands(brandsList));
        dispatch(setIsBrandsFetching(false));

        const defaultBrand = brandsList.find((b: BrandResource) => b.is_default);
        if (defaultBrand) {
            dispatch(setBrand(defaultBrand));
        } else if (brandsList.length > 0) {
            dispatch(setBrand(brandsList[0]));
        }

        return true;
    } catch (err) {
        dispatch(setIsBrandsFetching(false));
        dispatch(setBrandsFetchingError(getErrorMessage(err)));
        return false;
    }
};

export const fetchDefaultBrand = (accountId: string) => async (dispatch: any) => {
    dispatch(setIsBrandFetching(true));
    dispatch(setBrandFetchingError(null));

    try {
        const result = await brandApi.getDefaultBrand(accountId) as any;
        dispatch(setBrand(result.data));
        dispatch(setIsBrandFetching(false));
        return true;
    } catch {
        dispatch(setBrand(null));
        dispatch(setIsBrandFetching(false));
        return false;
    }
};

export const saveBrand = (accountId: string, brandId: string, data: UpdateBrandRequest) => async (dispatch: any) => {
    dispatch(setIsBrandSaving(true));
    dispatch(setBrandSavingError(null));

    try {
        const result = await brandApi.updateBrand(accountId, brandId, data) as any;
        dispatch(setBrand(result.data));
        dispatch(setIsBrandSaving(false));
        return true;
    } catch (err) {
        dispatch(setIsBrandSaving(false));
        dispatch(setBrandSavingError(getErrorMessage(err)));
        return false;
    }
};

export const createDefaultBrand = (accountId: string, data: CreateBrandRequest) => async (dispatch: any) => {
    dispatch(setIsBrandSaving(true));
    dispatch(setBrandSavingError(null));

    try {
        const result = await brandApi.createBrand(accountId, data) as any;
        dispatch(setBrand(result.data));
        dispatch(setIsBrandSaving(false));
        return true;
    } catch (err) {
        dispatch(setIsBrandSaving(false));
        dispatch(setBrandSavingError(getErrorMessage(err)));
        return false;
    }
};

export const saveLogo = (accountId: string, brandId: string, data: UpdateLogoRequest) => async (dispatch: any) => {
    dispatch(setIsLogoSaving(true));
    dispatch(setLogoSavingError(null));

    try {
        const result = await brandApi.updateLogo(accountId, brandId, data) as any;
        dispatch(setBrand(result.data));
        dispatch(setIsLogoSaving(false));
        return true;
    } catch (err) {
        dispatch(setIsLogoSaving(false));
        dispatch(setLogoSavingError(getErrorMessage(err)));
        return false;
    }
};

export const selectBrands = (state: any) => state.brandSettings.brands;
export const selectBrand = (state: any) => state.brandSettings.brand;
export const selectIsBrandsFetching = (state: any) => state.brandSettings.isBrandsFetching;
export const selectBrandsFetchingError = (state: any) => state.brandSettings.brandsFetchingError;
export const selectIsBrandFetching = (state: any) => state.brandSettings.isBrandFetching;
export const selectBrandFetchingError = (state: any) => state.brandSettings.brandFetchingError;
export const selectIsBrandSaving = (state: any) => state.brandSettings.isBrandSaving;
export const selectBrandSavingError = (state: any) => state.brandSettings.brandSavingError;
export const selectIsLogoSaving = (state: any) => state.brandSettings.isLogoSaving;
export const selectLogoSavingError = (state: any) => state.brandSettings.logoSavingError;

export default brandSettingsSlice.reducer;
